import AppBridgeHelper from "helpers/AppBridgeHelper";

/**
 * Pure WebSocket Gateway Service for React POS.
 * Handles incoming connection events from the Windows host, tracks active clients,
 * and provides clean message routing and outbound transport.
 */
class WebSocketGatewayService {
  constructor() {
    this.clients = new Map();
    this.listeners = new Map();
  }

  /**
   * Main entry point called by window.nativeApi.onWebSocketEvent
   */
  handleNativeEvent(eventType, connectionId, data) {
    if (!connectionId) return;

    if (eventType === "connected") {
      this.clients.set(connectionId, { connectionId, remoteIp: data || null });
      return;
    }

    if (eventType === "disconnected") {
      this.clients.delete(connectionId);
      return;
    }

    if (eventType === "message") {
      let parsed = data;
      if (typeof data === "string") {
        try {
          parsed = JSON.parse(data);
        } catch (e) {
          parsed = data;
        }
      }

      const action = parsed?.action || parsed?.type;
      const context = {
        connectionId,
        reply: (replyData) => this.sendToClient(connectionId, replyData),
      };

      // Built-in infrastructure responder for ping
      if (action === "ping" || action === "PING") {
        context.reply({ type: "PONG", timestamp: Date.now() });
        return;
      }

      // Notify registered action listeners
      if (action && this.listeners.has(action)) {
        this.listeners.get(action).forEach((callback) => {
          try {
            callback(parsed?.payload ?? parsed, context);
          } catch (err) {
            console.error(`[WebSocket] Error handling ${action}:`, err);
          }
        });
      }
    }
  }

  /**
   * Sends a message to a specific client
   */
  async sendToClient(connectionId, payload) {
    try {
      const bridge = AppBridgeHelper.getBridge();
      if (bridge?.sendToWebSocketClient) {
        const rawJson = typeof payload === "string" ? payload : JSON.stringify(payload);
        return await bridge.sendToWebSocketClient(connectionId, rawJson);
      }
      return false;
    } catch (err) {
      console.error(`[WebSocket] Send to ${connectionId} failed:`, err);
      return false;
    }
  }

  /**
   * Broadcasts a message to all connected clients
   */
  broadcast(payload, excludeConnectionId = null) {
    try {
      const bridge = AppBridgeHelper.getBridge();
      if (bridge?.broadcastWebSocket) {
        const rawJson = typeof payload === "string" ? payload : JSON.stringify(payload);
        bridge.broadcastWebSocket(rawJson, excludeConnectionId);
      }
    } catch (err) {
      console.error("[WebSocket] Broadcast failed:", err);
    }
  }

  /**
   * Subscribes to an incoming action. Returns unsubscribe function.
   */
  on(action, callback) {
    if (!this.listeners.has(action)) {
      this.listeners.set(action, new Set());
    }
    this.listeners.get(action).add(callback);

    return () => {
      const set = this.listeners.get(action);
      if (set) {
        set.delete(callback);
        if (set.size === 0) this.listeners.delete(action);
      }
    };
  }

  getClients() {
    return Array.from(this.clients.values());
  }
}

const instance = new WebSocketGatewayService();
export default instance;
